import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Target, Plus, Pencil, Copy, Trash2, Users, Star, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function OfferProfiles() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/offer-profiles");
      setProfiles(data.profiles || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    try {
      await api.post(`/offer-profiles/${id}/duplicate`);
      toast.success("Profile duplicated");
      await load();
      await refreshProfile();
    } catch (e) {
      toast.error("Could not duplicate profile");
    }
  };

  const confirmDelete = async () => {
    const id = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/offer-profiles/${id}`);
      toast.success("Profile deleted");
      await load();
      await refreshProfile();
    } catch (e) {
      toast.error("Could not delete profile");
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.post(`/offer-profiles/${id}/set-default`);
      toast.success("Default offer updated");
      await load();
    } catch (e) {
      toast.error("Could not set default");
    }
  };

  return (
    <div className="space-y-8" data-testid="offer-profiles-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div className="fade-up flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Target size={24} />
          </div>
          <div>
            <h1 className="font-head text-4xl font-bold">Offer Profiles</h1>
            <p className="text-muted-foreground">
              One profile per offer or niche. Campaigns build from the profile you pick.
            </p>
          </div>
        </div>
        <Button onClick={() => navigate("/offer-profiles/new")} data-testid="new-profile-btn"
          className="rounded-full bg-primary px-6 py-6 text-base font-semibold hover:bg-primary/90">
          <Plus size={18} className="mr-1" /> New profile
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : profiles.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-4 border-dashed border-border p-12 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary">
            <Target size={26} className="text-primary" />
          </div>
          <div>
            <p className="font-head text-lg font-semibold">No offer profiles yet</p>
            <p className="text-sm text-muted-foreground">Create your first one to power hooks and campaigns.</p>
          </div>
          <Button onClick={() => navigate("/offer-profiles/new")} data-testid="empty-new-profile-btn"
            className="rounded-full bg-primary hover:bg-primary/90">
            <Plus size={18} className="mr-1" /> Create profile
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {profiles.map((p) => (
            <Card key={p.id} data-testid={`profile-card-${p.id}`}
              className={`group flex flex-col border-border p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg ${
                p.is_default ? "ring-2 ring-primary/40" : ""
              }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-block rounded-md bg-secondary px-2 py-0.5 text-xs font-medium capitalize text-secondary-foreground">
                      {p.offer_type}
                    </span>
                    {p.is_default && (
                      <span data-testid={`default-badge-${p.id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        <Star size={11} className="fill-primary" /> Default
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 truncate font-head text-xl font-bold">{p.name}</h3>
                </div>
                <button onClick={() => makeDefault(p.id)} disabled={p.is_default}
                  data-testid={`star-profile-${p.id}`}
                  title={p.is_default ? "This is your default offer" : "Set as default"}
                  className={`shrink-0 rounded-lg p-2 transition-colors ${
                    p.is_default ? "text-primary" : "text-muted-foreground hover:bg-secondary hover:text-primary"
                  }`}>
                  <Star size={18} className={p.is_default ? "fill-primary" : ""} />
                </button>
              </div>
              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.selling}</p>
              <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                <Users size={15} className="mt-0.5 shrink-0" />
                <span className="line-clamp-1">{p.audience || "No audience set"}</span>
              </div>
              <button onClick={() => navigate(`/pitch-test/${p.id}`)} data-testid={`pitch-test-${p.id}`}
                className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  p.pitch_test_done
                    ? "bg-accent/15 text-accent hover:bg-accent/25"
                    : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                }`}>
                <Megaphone size={12} />
                {p.pitch_test_done ? "Pitch Test done · view" : "Run Pitch Test"}
              </button>
              <div className="mt-5 flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => navigate(`/offer-profiles/${p.id}`)}
                  data-testid={`edit-profile-${p.id}`} className="rounded-full">
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(p.id)}
                  data-testid={`duplicate-profile-${p.id}`} className="rounded-full">
                  <Copy size={14} className="mr-1" /> Duplicate
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setToDelete(p.id)}
                  data-testid={`delete-profile-${p.id}`}
                  className="ml-auto rounded-full text-muted-foreground hover:text-destructive">
                  <Trash2 size={15} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-profile-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Offer Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This can't be undone. Campaigns already created from it keep their saved details.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-profile">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-profile"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
